import React from 'react';
import { useTranslation } from 'react-i18next';
import { PaperProps } from '@mui/material';
import Box, { BoxProps } from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { Breakpoint, useTheme } from '@mui/material/styles';
import { IconX } from '@tabler/icons-react';

import { getAppCuesId } from 'src/utils/appCuesUtil';

export interface BaseModalProps {
  children: React.ReactNode;
  featureName?: string;
  hasScrolled?: boolean;
  HeaderProps?: BoxProps;
  headerTitle?: React.ReactNode;
  hideCloseButton?: boolean; // only call onClose when clicking outside
  isOpen: boolean;
  maxWidth?: Breakpoint;
  onClose?: () => void;
  PaperProps?: PaperProps;
  zIndex?: number;
}

const BaseModal = ({
  children,
  featureName,
  hasScrolled,
  HeaderProps,
  headerTitle,
  hideCloseButton = false,
  isOpen,
  maxWidth = 'md',
  onClose,
  PaperProps,
  zIndex,
}: BaseModalProps) => {
  const { t } = useTranslation('common');
  const { palette } = useTheme();
  return (
    <Dialog
      data-appcues-target={getAppCuesId({
        componentType: 'modal',
        featureName: featureName || '',
        pageName: 'discover',
        subType: '',
      })}
      fullWidth
      maxWidth={maxWidth}
      onClose={onClose}
      open={isOpen}
      slotProps={{
        paper: PaperProps,
      }}
      {...(zIndex !== undefined && { sx: { zIndex } })}
    >
      <Box
        sx={[
          {
            alignItems: 'center',
            color: palette.colors.black,
            display: 'flex',
            padding: 3,
          },
          headerTitle
            ? { justifyContent: 'space-between' }
            : { justifyContent: 'flex-end' },
          Boolean(hasScrolled) && {
            borderBottom: `1px solid ${palette.colors.slate[300]}`,
          },
        ]}
        {...HeaderProps}
      >
        {headerTitle}
        {onClose && !hideCloseButton && (
          <IconButton
            aria-label={t('baseModal.closeAriaLabel')}
            onClick={onClose}
          >
            <IconX />
          </IconButton>
        )}
      </Box>
      {children}
    </Dialog>
  );
};

export default BaseModal;
